// @ts-check--
import React from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { withErrorBoundary } from "react-error-boundary";
import { Box, HStack, VStack, Skeleton, LinkBox, LinkOverlay } from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";

import { FormattedMoney } from "components/FormattedMoney";
import { formatTransactionBasedOnTransactionType } from "../utils";

const FALLBACK_DESCRIPTION = "Transaction";

/**
 * TransactionItem
 * @description
 * Presentational component for displaying the bare minimum of transaction details. Only used
 * in Transaction History for now.
 * @param {object} props
 * @param {string} props.description
 * @param {string} props.subDescription
 * @param {string} props.amount
 * @param {string} props.amountColor
 * @param {string} [props.transactionId]
 * @param {boolean} [props.isPending]
 * @param {boolean} [props.isFailed]
 * @param {Element} [props.statusDescription]
 * @returns JSX.Element
 */
export const TransactionItem = ({
  description,
  subDescription,
  amount,
  currency,
  amountColor,
  transactionId,
  isPending = false,
  isFailed = false,
  localizedStatus = null,
}) => (
  <LinkBox
    flex={1}
    as="li"
    aria-label={`${isPending ? "Pending" : subDescription} transaction for ${amount}`}
    listStyleType="none"
  >
    <HStack flex={1} justifyContent="space-between" alignItems="center">
      <VStack alignItems="flex-start" spacing={1} overflow="hidden">
        <Box
          fontSize="sm"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          maxWidth="100%"
          pr={4}
        >
          {transactionId ? (
            <LinkOverlay as={Link} to={`/txnhistory/${transactionId}`}>
              {description || FALLBACK_DESCRIPTION}
            </LinkOverlay>
          ) : (
            <>{description || FALLBACK_DESCRIPTION}</>
          )}
        </Box>

        <Box fontSize="xs" color="tertiaryText">
          {subDescription}
        </Box>
      </VStack>

      <VStack alignItems="flex-end" spacing={1} flexShrink={0}>
        <Box
          fontSize="sm"
          fontWeight="medium"
          color={isFailed ? "primaryText" : amountColor}
          textDecoration={isFailed ? "line-through" : "initial"}
        >
          {currency && Number.isInteger(amount) ? (
            <FormattedMoney value={amount} currency={currency} />
          ) : (
            amount || <FormattedMessage defaultMessage="N/A" description="Not applicable." />
          )}
        </Box>

        {isFailed ? (
          <HStack spacing={2} role="alert" aria-label="Transaction marked as failed">
            <WarningTwoIcon w={3} h={3} color="errorText" />
            <Box fontSize="xs" color="tertiaryText">
              {localizedStatus || (
                <FormattedMessage defaultMessage="Failed" description="Status description" />
              )}
            </Box>
          </HStack>
        ) : null}
      </VStack>
    </HStack>
  </LinkBox>
);

/**
 * TransactionItemSkeleton
 * @description
 * The skeleton component for a single transaction item.
 */
export const TransactionItemSkeleton = () => {
  return (
    <HStack flex={1} justifyContent="space-between" alignItems="center">
      <VStack flex={1} alignItems="flex-start" spacing={2}>
        <Skeleton height="18px" width="100%" />
        <Skeleton height="14px" width="20%" />
      </VStack>
      <VStack flex={1} alignItems="flex-end">
        <Skeleton height="18px" width="20%" />
      </VStack>
    </HStack>
  );
};

/**
 * FallbackComponent
 * @description
 * Component rendered if there's an exception within the transaction item component.
 */
const FallbackComponent = () => {
  return (
    <Box
      flex={1}
      borderLeftWidth={4}
      borderLeftColor="errorText"
      pl={4}
      role="alert"
      aria-label="Something went wrong. Unable to render transaction. This event has been logged."
    >
      <VStack alignItems="flex-start" spacing={1}>
        <Box fontSize="sm" fontWeight="bold" color="errorText">
          <FormattedMessage defaultMessage="Something went wrong" description="Error title" />
        </Box>
        <Box fontSize="xs" color="errorText">
          <FormattedMessage
            defaultMessage="Unable to render transaction. This event has been logged."
            description="Error message"
          />
        </Box>
      </VStack>
    </Box>
  );
};

/**
 * FormattedTransactionItem
 * @description
 * Takes in a transaction and figures out how to display it based on the transaction type.
 * @param {object} props
 * @param {Transaction} props.transaction
 * @param {string} props.accountId Account ID of the current user
 * @returns JSX.Element
 */
const FormattedTransactionItem = ({ transaction, accountId }) => {
  const { formatMessage, formatDate } = useIntl();
  const { description, type, amount, amountColor, currency, localizedStatus } =
    formatTransactionBasedOnTransactionType(transaction, accountId, formatMessage);
  const failedStatuses = ["failed", "payout-failed", "error"];
  const isFailed = failedStatuses.some((status) => transaction.transaction_status === status);
  const isPending = transaction.transaction_status === "pending";

  return (
    <TransactionItem
      description={description}
      subDescription={
        isPending
          ? formatDate(new Date(transaction.transaction_date), {
              month: "long",
              day: "numeric",
            })
          : type
      }
      amount={amount}
      currency={currency}
      amountColor={amountColor}
      transactionId={transaction.transaction_id}
      isPending={isPending}
      isFailed={isFailed}
      localizedStatus={localizedStatus}
    />
  );
};

export default withErrorBoundary(FormattedTransactionItem, {
  FallbackComponent,
  onError() {},
});
